/**
 * Camps — the browser's client for `/api/camps…` (backend routes/camps.ts).
 *
 * Its own small fetch rather than lib/api.ts `apiJson`, because Camps are open
 * to signed-out readers: a 401 here (a private Camp, a write while signed out)
 * is an answer to show, not a reason to navigate to /login. The sign-in itself
 * renews on every /api request server-side (lib/auth.ts authMiddleware).
 */

import type { Art } from '@/components/camps/CampArt';

const API = process.env.NEXT_PUBLIC_API_BASE ?? '';

export class CampsError extends Error {
  constructor(message: string, public status: number, public code?: string) { super(message); this.name = 'CampsError'; }
}

async function call<T>(path: string, init?: RequestInit & { json?: unknown }): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API}/api${path}`, {
      credentials: 'include',
      cache: 'no-store',
      ...init,
      method: init?.method ?? (init?.json !== undefined ? 'POST' : 'GET'),
      headers: init?.json !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      body: init?.json !== undefined ? JSON.stringify(init.json) : undefined,
    });
  } catch {
    throw new CampsError('Could not reach the server. Check your connection and try again.', 0);
  }
  let body: any = {};
  try { body = await res.json(); } catch { /* empty */ }
  if (!res.ok) throw new CampsError(body?.error || `That did not work (${res.status}).`, res.status, body?.code);
  return body as T;
}

export type CampType = 'public' | 'restricted' | 'private';
export type MyStatus = 'none' | 'member' | 'pending' | 'banned';

/** `level` 1–20 and `rank` are the public reputation rank; `kharma` the KHARMA they earned from upvotes (backend lib/reputation.ts). */
export interface Person { id: string; name: string; avatar: string | null; demo: boolean; system?: boolean; level: number; rank: string; kharma: number }
export type AwardKind = 'thanks' | 'great' | 'legendary';
export type Awards = Partial<Record<AwardKind, number>>;
export type CqsTier = 'lowest' | 'low' | 'moderate' | 'high' | 'highest';
export type MinCqs = 'none' | 'low' | 'moderate' | 'high';
export interface Flair { id: string; text: string; color: string }
export interface Rule { title: string; body: string }

export interface CampCard {
  id: string; slug: string; name: string; title: string; description: string; type: CampType; official: boolean; art: Art;
  memberCount: number; postCount: number; createdAt: string; closed: boolean; deleted: boolean; demo: boolean;
  postsThisWeek?: number; myStatus?: MyStatus; isMod?: boolean;
  /** What it is about (CAMP_TOPICS), for Explore. */
  topic?: CampTopic;
}
/** The Explore topics, in display order (the backend's CAMP_TOPICS). */
export const CAMP_TOPICS = [
  { id: 'ai', label: 'AI & Data' },
  { id: 'freelancing', label: 'Freelancing' },
  { id: 'careers', label: 'Jobs & Careers' },
  { id: 'money', label: 'Money & Payments' },
  { id: 'languages', label: 'Languages' },
  { id: 'games', label: 'Games' },
  { id: 'tech', label: 'Technology' },
  { id: 'community', label: 'Community' },
] as const;
export type CampTopic = (typeof CAMP_TOPICS)[number]['id'];
export interface ExploreTopic { id: CampTopic; label: string; camps: CampCard[] }
export interface Access {
  signedIn: boolean; isAdmin: boolean; isOwner: boolean; isMod: boolean; status: MyStatus; approved: boolean; banReason: string;
  canRead: boolean; canPost: boolean; canComment: boolean; canVote: boolean; canJoin: boolean; postBlock: string;
}
export interface CampDetail extends CampCard {
  rules: Rule[]; flairs: Flair[]; owner: Person | null; moderators: Person[]; commentCount: number; access: Access;
  /** CQS filter, the KHARMA earned here needed to post, and what the viewer has earned here. */
  minCqs: MinCqs; minKharma: number; myKharma: number;
}
export interface Post {
  id: string; campId: string; camp: { slug: string; name: string; art: Art; official: boolean };
  title: string; kind: 'text' | 'link' | 'game' | 'media'; body: string; url: string; domain: string; flair: Flair | null;
  /** A game post: the shelf game that plays inside it (components/camps/GamePlayer.tsx). */
  game: string | null;
  /** A media post's gallery, in order (components/camps/MediaView.tsx). */
  media: Media[];
  author: Person | null; score: number; commentCount: number; createdAt: string; editedAt: string | null;
  pinned: boolean; locked: boolean; deleted: boolean; removed: boolean; removedReason: string; mine: boolean; voted: boolean; vote: VoteDir; downs: number; demo: boolean;
  awards: Awards; held: boolean; authorCqs?: CqsTier | null;
}
export interface Comment {
  id: string; postId: string; parentId: string | null; depth: number; body: string; author: Person | null;
  score: number; replyCount: number; createdAt: string; editedAt: string | null; deleted: boolean; removed: boolean;
  removedReason: string; mine: boolean; voted: boolean; vote: VoteDir; downs: number; demo: boolean;
  awards: Awards; held: boolean; authorCqs?: CqsTier | null;
}
/** One image or video on a post (backend lib/media.ts mediaJson). */
export interface Media {
  id: string; kind: 'image' | 'video'; w: number; h: number; dur: number; blur: string; caption: string;
  /** The image (or a video's poster) by width, smallest first. */
  src: { w: number; url: string }[];
  full: string;
  video: string | null;
  silent: boolean;
}
/** An upload in the composer, while it is sent and processed. */
export interface Upload extends Media { status: 'processing' | 'ready' | 'failed'; error: string }

export const MEDIA_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,image/avif,image/heic,image/heif,video/mp4,video/quicktime,video/webm';
export const MEDIA_MAX = { perPost: 20, imageMB: 20, videoMB: 60 };

/**
 * Send one file. XHR rather than fetch, for the upload progress (0..1).
 * Resolves with the new upload (`processing`, or `ready` for a file already stored).
 */
export function uploadMedia(file: File, onProgress: (p: number) => void, signal?: AbortSignal): Promise<Upload> {
  return new Promise((ok, bad) => {
    const x = new XMLHttpRequest();
    x.open('POST', `${API}/api/camps-media`);
    x.withCredentials = true;
    x.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    x.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded / e.total); };
    x.onload = () => {
      let body: any = {};
      try { body = JSON.parse(x.responseText); } catch { /* empty */ }
      if (x.status >= 200 && x.status < 300 && body.media) ok(body.media);
      else bad(new CampsError(body?.error || `The upload failed (${x.status}).`, x.status, body?.code));
    };
    x.onerror = () => bad(new CampsError('The upload failed. Check your connection and try again.', 0));
    x.onabort = () => bad(new CampsError('Cancelled.', 0, 'aborted'));
    signal?.addEventListener('abort', () => x.abort());
    x.send(file);
  });
}
export const mediaStatus = (id: string) => call<{ media: Upload }>(`/camps-media/${enc(id)}`).then((r) => r.media);

export type Sort = 'hot' | 'new' | 'top';
export type Window = 'day' | 'week' | 'month' | 'all';

const qs = (o: Record<string, string | undefined | null>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v) p.set(k, v);
  const s = p.toString();
  return s ? `?${s}` : '';
};
const enc = encodeURIComponent;

// Directory
export const listCamps = (tab: 'popular' | 'new' | 'mine', q: string, cursor?: string | null) =>
  call<{ camps: CampCard[]; next: string | null; signedIn: boolean }>(`/camps${qs({ tab, q, cursor })}`);
export const checkCampName = (name: string) => call<{ ok: boolean; error?: string }>(`/camps/check${qs({ name })}`);
export const exploreCamps = () => call<{ recommended: CampCard[]; topics: ExploreTopic[]; signedIn: boolean }>('/camps-explore');
export const createCamp = (b: { name: string; title: string; description: string; type: CampType; art: Art; topic: CampTopic }) =>
  call<{ camp: CampCard }>('/camps', { json: b });
export const fetchFeed = (scope: 'home' | 'popular', sort: Sort, t: Window, cursor?: string | null) =>
  call<{ posts: Post[]; next: string | null; signedIn: boolean }>(`/camps/feed${qs({ scope, sort, t: sort === 'top' ? t : null, cursor })}`);

// One Camp
export const fetchCamp = (slug: string) => call<{ camp: CampDetail }>(`/camps/${enc(slug)}`);
export const updateCamp = (slug: string, b: Partial<{ title: string; description: string; type: CampType; art: Art; rules: Rule[]; flairs: Partial<Flair>[]; minCqs: MinCqs; minKharma: number; topic: CampTopic }>) =>
  call<{ camp: CampDetail }>(`/camps/${enc(slug)}`, { method: 'PATCH', json: b });
export const deleteCamp = (slug: string, confirm: string) => call<{ ok: true }>(`/camps/${enc(slug)}`, { method: 'DELETE', json: { confirm } });
export const joinCamp = (slug: string) => call<{ status: MyStatus; memberCount: number }>(`/camps/${enc(slug)}/join`, { json: {} });
export const leaveCamp = (slug: string) => call<{ status: MyStatus; memberCount: number }>(`/camps/${enc(slug)}/leave`, { json: {} });

// Posts
export const fetchPosts = (slug: string, sort: Sort, t: Window, flair: string, cursor?: string | null) =>
  call<{ pinned: Post[]; posts: Post[]; next: string | null }>(`/camps/${enc(slug)}/posts${qs({ sort, t: sort === 'top' ? t : null, flair, cursor })}`);
export const createPost = (slug: string, b: { title: string; kind: 'text' | 'link' | 'game' | 'media'; body?: string; url?: string; game?: string; flair?: string; media?: { id: string; caption: string }[] }) =>
  call<{ post: Post }>(`/camps/${enc(slug)}/posts`, { json: b });
export const fetchPost = (slug: string, id: string) => call<{ post: Post; camp: CampDetail }>(`/camps/${enc(slug)}/posts/${enc(id)}`);
export const editPost = (slug: string, id: string, b: { body?: string; flair?: string }) =>
  call<{ post: Post }>(`/camps/${enc(slug)}/posts/${enc(id)}`, { method: 'PATCH', json: b });
export const deletePost = (slug: string, id: string) => call<{ ok: true }>(`/camps/${enc(slug)}/posts/${enc(id)}`, { method: 'DELETE' });
/** A vote, Reddit style: 1 up, -1 down, 0 none. `score` is up − down. */
export type VoteDir = 1 | 0 | -1;
export type VoteResult = { vote: VoteDir; voted: boolean; score: number; downs: number };
export const votePost = (slug: string, id: string, dir: VoteDir) => call<VoteResult>(`/camps/${enc(slug)}/posts/${enc(id)}/vote`, { json: { dir } });

/** The official post where a game is played in Camps (c/games), made the first time it is asked for. */
export const officialGamePost = (gameId: string) => call<{ post: { slug: string; id: string; title: string } }>(`/camps-games/${enc(gameId)}`);

// Comments
export const fetchComments = (slug: string, postId: string, sort: 'top' | 'new', cursor?: string | null, thread?: string | null) =>
  call<{ comments: Comment[]; next: string | null; thread: { id: string; parentId: string | null; depth: number } | null; renderDepth: number }>(
    `/camps/${enc(slug)}/posts/${enc(postId)}/comments${qs({ sort, cursor, thread })}`);
export const createComment = (slug: string, postId: string, body: string, parentId?: string | null) =>
  call<{ comment: Comment }>(`/camps/${enc(slug)}/posts/${enc(postId)}/comments`, { json: { body, parentId: parentId ?? undefined } });
export const editComment = (slug: string, id: string, body: string) => call<{ comment: Comment }>(`/camps/${enc(slug)}/comments/${enc(id)}`, { method: 'PATCH', json: { body } });
export const deleteComment = (slug: string, id: string) => call<{ ok: true }>(`/camps/${enc(slug)}/comments/${enc(id)}`, { method: 'DELETE' });
export const voteComment = (slug: string, id: string, dir: VoteDir) => call<VoteResult>(`/camps/${enc(slug)}/comments/${enc(id)}/vote`, { json: { dir } });

// Awards (backend lib/reputation.ts): KHARMA from you; the author keeps a share that grows with the KHARMA they earned from upvotes.
export const AWARD_LIST: { id: AwardKind; label: string; price: number }[] = [
  { id: 'thanks', label: 'Thanks', price: 100 },
  { id: 'great', label: 'Great', price: 500 },
  { id: 'legendary', label: 'Legendary', price: 2500 },
];
export const awardTarget = (slug: string, type: 'post' | 'comment', id: string, award: AwardKind) =>
  call<{ awards: Awards; balance: number; paid: number }>(`/camps/${enc(slug)}/${type === 'post' ? 'posts' : 'comments'}/${enc(id)}/award`, { json: { award } });

// Profiles: u/name
export interface Profile {
  person: {
    /** The account id (for Chat). */
    id: string; system?: boolean;
    name: string; avatar: string | null; demo: boolean; cakeDay: string | null; disabled: boolean; me: boolean;
    /** Lifetime KHARMA by source; `fromUpvotes` = post + comment. */
    kharma: { post: number; comment: number; awardsIn: number; awardsOut: number; fromUpvotes: number };
    contributor: { tier: 'visitor' | 'contributor' | 'top'; label: string; keep: number };
    rank: { level: number; name: string; points: number; next: { level: number; name: string; min: number } | null; lines?: string[] };
  };
  camps: { slug: string; name: string; art: Art; official: boolean; post: number; comment: number }[];
  posts: Post[];
  comments: (Comment & { post: { id: string; title: string } | null; camp: { slug: string; name: string } | null })[];
}
export const fetchProfile = (name: string) => call<Profile>(`/people/${enc(name)}`);

// Reports
export const report = (slug: string, targetType: 'post' | 'comment' | 'camp', targetId: string, reason: string) =>
  call<{ ok: true; already?: boolean }>(`/camps/${enc(slug)}/report`, { json: { targetType, targetId, reason } });

// Moderation
export interface ReportRow {
  id: string; targetType: 'post' | 'comment' | 'camp'; targetId: string; reason: string; status: string; createdAt: string; resolvedBy: string;
  reporter: string | null; reporterAvatar?: string | null; camp: { slug: string; name: string; art: Art; official: boolean } | null;
  target: { title: string | null; body: string; url: string; author: string | null; authorAvatar?: string | null; authorCqs?: CqsTier | null; removed: boolean; deleted: boolean; postId: string } | null;
}
export interface MemberRow { userId: string; person: Person | null; status: MyStatus; approved: boolean; banReason: string; joinedAt: string; isMod: boolean; isOwner: boolean }
export interface LogRow { id: string; actor: string; actorAvatar?: string | null; source: string; action: string; targetType: string; targetId: string | null; details: string; createdAt: string }

/** A name plus a relative `/api/avatar/<key>` (the mod queue's flat fields), shaped for the `Avatar` in bits.tsx. */
export const face = (name: string, avatar?: string | null) => ({ name, avatar: avatar ? API + avatar : null });

export const modReports = (slug: string) => call<{ reports: ReportRow[] }>(`/camps/${enc(slug)}/mod/reports`);
export const modRemoved = (slug: string) => call<{ posts: Post[]; comments: Comment[] }>(`/camps/${enc(slug)}/mod/removed`);
export const modMembers = (slug: string, status: MyStatus, q: string, cursor?: string | null) =>
  call<{ members: MemberRow[]; next: string | null }>(`/camps/${enc(slug)}/mod/members${qs({ status, q, cursor })}`);
export const modLogList = (slug: string, cursor?: string | null) => call<{ log: LogRow[]; next: string | null }>(`/camps/${enc(slug)}/mod/log${qs({ cursor })}`);
export const modPost = (slug: string, id: string, action: 'remove' | 'approve' | 'pin' | 'unpin' | 'lock' | 'unlock', reason?: string) =>
  call<{ post: Post }>(`/camps/${enc(slug)}/mod/posts/${enc(id)}`, { json: { action, reason } });
export const modComment = (slug: string, id: string, action: 'remove' | 'approve', reason?: string) =>
  call<{ comment: Comment }>(`/camps/${enc(slug)}/mod/comments/${enc(id)}`, { json: { action, reason } });
export const modMember = (slug: string, who: { userId?: string; nickname?: string }, action: 'ban' | 'unban' | 'approve' | 'reject' | 'approve_poster' | 'revoke_poster', reason?: string) =>
  call<{ status: MyStatus; approved: boolean }>(`/camps/${enc(slug)}/mod/members`, { json: { ...who, action, reason } });
export const modReport = (slug: string, id: string, action: 'dismiss' | 'remove') => call<{ ok: true }>(`/camps/${enc(slug)}/mod/reports/${enc(id)}`, { json: { action } });
export const addModerator = (slug: string, nickname: string) => call<{ ok: true }>(`/camps/${enc(slug)}/mod/moderators`, { json: { nickname } });
export const removeModerator = (slug: string, userId: string) => call<{ ok: true }>(`/camps/${enc(slug)}/mod/moderators/${enc(userId)}`, { method: 'DELETE' });

// Helpers shared by the pages
export const postHref = (p: { id: string; title: string; camp: { slug: string } }) => {
  const words = p.title.toLowerCase().normalize('NFKD').replace(/[^\p{L}\p{N}]+/gu, '_').replace(/^_+|_+$/g, '').slice(0, 60).replace(/_+$/, '');
  return `/c/${p.camp.slug}/p/${p.id}${words ? `/${words}` : ''}`;
};
export const signInHref = () => (typeof window === 'undefined' ? '/login' : `/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);
